export interface TextStats {
  characters: number;
  charactersNoSpaces: number;
  words: number;
  lines: number;
}

export function toUppercase(value: string): string {
  return value.toUpperCase();
}

export function toLowercase(value: string): string {
  return value.toLowerCase();
}

export function toTitleCase(value: string): string {
  return toWords(value)
    .map((word) => capitalize(word.toLowerCase()))
    .join(' ');
}

export function toSentenceCase(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  const lower = trimmed.toLowerCase();
  return `${lower.charAt(0).toUpperCase()}${lower.slice(1)}`;
}

export function toCamelCase(value: string): string {
  return toWords(value)
    .map((word, index) => {
      const normalized = word.toLowerCase();
      return index === 0 ? normalized : capitalize(normalized);
    })
    .join('');
}

export function toPascalCase(value: string): string {
  return toWords(value)
    .map((word) => capitalize(word.toLowerCase()))
    .join('');
}

export function toSnakeCase(value: string): string {
  return toWords(value)
    .map((word) => word.toLowerCase())
    .join('_');
}

export function toKebabCase(value: string): string {
  return toWords(value)
    .map((word) => word.toLowerCase())
    .join('-');
}

export function reverseText(value: string): string {
  return Array.from(value).reverse().join('');
}

export function trimWhitespace(value: string): string {
  return value.trim();
}

export function removeSpaces(value: string): string {
  return value.replace(/\s+/g, '');
}

export function getTextStats(value: string): TextStats {
  const characters = Array.from(value).length;
  const charactersNoSpaces = Array.from(value.replace(/\s/g, '')).length;
  const words = value.trim() ? value.trim().split(/\s+/).length : 0;
  const lines = value.length === 0 ? 0 : value.split(/\r\n|\r|\n/).length;

  return { characters, charactersNoSpaces, words, lines };
}

export interface DiffRow {
  index: number;
  left: string | null;
  right: string | null;
  matches: boolean;
}

export function buildDiffRows(
  leftValue: string,
  rightValue: string,
  caseSensitive = true,
): DiffRow[] {
  const leftChars = Array.from(leftValue);
  const rightChars = Array.from(rightValue);
  const maxLength = Math.max(leftChars.length, rightChars.length);

  return Array.from({ length: maxLength }, (_, index) => {
    const left = leftChars[index] ?? null;
    const right = rightChars[index] ?? null;

    return {
      index,
      left,
      right,
      matches:
        left !== null &&
        right !== null &&
        (caseSensitive ? left === right : left.toLowerCase() === right.toLowerCase()),
    };
  });
}

export function formatDiffChar(value: string | null): string {
  if (value === null) {
    return '∅';
  }

  if (value === ' ') {
    return '·';
  }

  if (value === '\n') {
    return '↵';
  }

  if (value === '\t') {
    return '⇥';
  }

  return value;
}

function toWords(value: string): string[] {
  return value.match(/[A-Za-z0-9]+/g) ?? [];
}

function capitalize(word: string): string {
  return word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : '';
}
